import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ArrowLeft, Clock, Keyboard, Lock } from 'lucide-react'
import { useConversations, type Conversation, type ListFilter } from '../api/inbox'
import { inboxShortcuts, useShortcuts } from '../lib/shortcuts'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useMe } from '../api/me'
import { useTags } from '../api/tags'
import { RequestFailed } from '../api/client'
import { ConversationPane } from '../components/ConversationPane'
import { initials, shortTime, useNow, windowState, bodyText, type WindowState } from '../lib/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

// The inbox: the list on the left, the open conversation on the right. At
// phone width only one of the two shows at a time.
export function InboxPage() {
  const { conversationId } = useParams()
  return (
    <main className="grid h-full grid-cols-[minmax(0,1fr)] md:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
      <ConversationList selected={conversationId} className={conversationId ? 'max-md:hidden' : undefined} />
      <section aria-label="Conversation" className={cn('flex min-h-0 min-w-0 flex-col bg-chat', !conversationId && 'max-md:hidden')}>
        {conversationId ? (
          <ConversationPane key={conversationId} id={conversationId} />
        ) : (
          <p className="m-auto max-w-xs p-6 text-center text-muted-foreground">Choose a conversation to read and reply.</p>
        )}
      </section>
    </main>
  )
}

// "Any" stands for no tag filter: Radix Select reserves the empty value.
const anyTag = 'any'

function ConversationList({ selected, className }: { selected: string | undefined; className: string | undefined }) {
  const me = useMe()
  const [filter, setFilter] = useState<ListFilter>({ status: 'open', assignee: 'all', tagId: undefined })
  const list = useConversations(filter)
  const tags = useTags()
  const now = useNow()
  const navigate = useNavigate()
  const isAgent = me.data?.user.role === 'agent'

  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const [help, setHelp] = useState(false)
  const step = (by: number) => {
    if (items.length === 0) return
    const at = items.findIndex((c) => c.id === selected)
    const next = at === -1 ? 0 : Math.min(items.length - 1, Math.max(0, at + by))
    const target = items[next]
    if (target && target.id !== selected) navigate(`/inbox/${target.id}`)
  }
  useShortcuts({ j: () => step(1), k: () => step(-1), '?': () => setHelp(true) })

  return (
    <aside aria-label="Conversations" className={cn('flex min-h-0 flex-col border-r bg-card', className)}>
      <div className="flex flex-col gap-3 border-b p-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-xl font-bold">Inbox</h1>
          <Button variant="ghost" size="sm" className="max-md:hidden text-muted-foreground" onClick={() => setHelp(true)}>
            <Keyboard className="size-4" aria-hidden="true" />
            Shortcuts
          </Button>
        </div>
        <Tabs value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v === 'closed' ? 'closed' : 'open' })}>
          <TabsList className="w-full">
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="closed">Closed</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2">
          <Select value={filter.assignee} onValueChange={(v) => setFilter({ ...filter, assignee: v })}>
            <SelectTrigger aria-label="Showing" className="min-w-0 flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isAgent ? 'Mine and unassigned' : 'Everyone'}</SelectItem>
              <SelectItem value="me">Assigned to me</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
          {tags.data && tags.data.length > 0 && (
            <Select value={filter.tagId ?? anyTag} onValueChange={(v) => setFilter({ ...filter, tagId: v === anyTag ? undefined : v })}>
              <SelectTrigger aria-label="Tag" className="min-w-0 flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={anyTag}>Any tag</SelectItem>
                {tags.data.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <ShortcutHelp open={help} onOpenChange={setHelp} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <ListSkeleton />
        ) : list.isError ? (
          <p role="alert" className="m-4 rounded-lg bg-destructive-soft p-3 text-sm text-destructive">
            {(list.error as RequestFailed).message}
          </p>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {filter.status === 'open'
              ? 'No open conversations here. New customer messages appear on their own.'
              : 'No closed conversations here.'}
          </p>
        ) : (
          <ul>
            {items.map((c) => {
              const w = windowState(c.serviceWindowExpiresAt, now)
              const current = c.id === selected
              return (
                <li key={c.id}>
                  <button
                    aria-current={current ? 'true' : undefined}
                    className={cn(
                      'flex w-full gap-3 border-b px-4 py-3 text-left hover:bg-accent',
                      current && 'bg-primary-soft shadow-[inset_3px_0_0_var(--primary)] hover:bg-primary-soft',
                    )}
                    onClick={() => navigate(`/inbox/${c.id}`)}
                  >
                    <Initials name={c.contact.name} />
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex justify-between gap-2">
                        <span className="truncate text-sm font-semibold">{c.contact.name}</span>
                        <time dateTime={c.lastMessageAt} className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {shortTime(c.lastMessageAt, now)}
                        </time>
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {c.lastMessage
                          ? `${previewWho(c.lastMessage)}${bodyText({ type: c.lastMessage.type, body: c.lastMessage.preview })}`
                          : 'No messages yet'}
                      </span>
                      <span className="flex flex-wrap items-center gap-1.5">
                        <WindowBadge w={w} short />
                        <span className="text-xs text-muted-foreground">{c.assignee ? c.assignee.name : 'Unassigned'}</span>
                        {c.contact.tags.map((t) => (
                          <Badge key={t.id} variant="outline">{t.name}</Badge>
                        ))}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {list.hasNextPage && (
          <div className="p-4">
            <Button variant="outline" className="w-full" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {list.isFetchingNextPage ? 'Loading' : 'Show older conversations'}
            </Button>
          </div>
        )}
      </div>
    </aside>
  )
}

function ListSkeleton() {
  return (
    <div role="status" aria-label="Loading conversations">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex gap-3 border-b px-4 py-3" aria-hidden="true">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex flex-1 flex-col gap-2 pt-1">
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function Initials({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground', className)}
    >
      {initials(name)}
    </span>
  )
}

// Whether a typed reply can be sent, in words and an icon, never colour alone.
export function WindowBadge({ w, short = false }: { w: WindowState; short?: boolean }) {
  const text = short ? w.short : w.label
  if (!w.open) {
    return (
      <Badge variant="neutral">
        <Lock aria-hidden="true" />
        {text}
      </Badge>
    )
  }
  return (
    <Badge variant={w.endingSoon ? 'attention' : 'success'}>
      <Clock aria-hidden="true" />
      {text}
    </Badge>
  )
}

// Who wrote the last message, before its text. Automatic replies say so:
// "You:" would claim a teammate wrote it.
function previewWho(m: NonNullable<Conversation['lastMessage']>): string {
  if (m.direction === 'inbound') return ''
  return m.autoReply ? 'Auto-reply: ' : 'You: '
}

function ShortcutHelp({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>They work in the inbox when you are not typing in a box.</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
          {inboxShortcuts.map((s) => (
            <div key={s.does} className="contents">
              <dt className="flex gap-1">
                {s.keys.map((k) => (
                  <kbd key={k} className="min-w-7 rounded-md border bg-muted px-1.5 py-0.5 text-center font-sans text-xs font-semibold">{k}</kbd>
                ))}
              </dt>
              <dd>{s.does}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  )
}

export function BackToList() {
  return (
    <Button asChild variant="ghost" size="icon" className="md:hidden">
      <Link to="/inbox" aria-label="Back to conversations">
        <ArrowLeft aria-hidden="true" />
      </Link>
    </Button>
  )
}
